<template>
    <v-col v-bind="DASHBOARD_WIDGET_COLUMNS.LARGE">
        <v-card>
            <v-toolbar color="grey-lighten-4" density="compact" flat>
                <template #title>{{ widget.body?.title }}</template>
            </v-toolbar>
            <v-divider />
            <v-card-text>
                <v-table density="compact">
                    <thead>
                        <tr>
                            <th />
                            <th
                                v-for="(header, index) in uniquePeriodHeaders"
                                :key="header"
                                class="text-center"
                                :class="periodCellClass(index)"
                                colspan="2"
                            >
                                {{ header }}
                            </th>
                        </tr>
                        <tr>
                            <th>Typ reklamacji</th>
                            <template v-for="(header, index) in uniquePeriodHeaders" :key="`${header}-sub`">
                                <th :class="periodCellClass(index)">Ilość / uznane</th>
                                <th :class="periodCellClass(index)">Kwota</th>
                            </template>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in rows" :key="String(row.type ?? '')">
                            <td>{{ row.type }}</td>
                            <template v-for="(header, index) in uniquePeriodHeaders" :key="`${row.type}-${header}`">
                                <td :class="periodCellClass(index)">{{ row[`period${index + 1}_quantity`] }}</td>
                                <td :class="periodCellClass(index)">{{ row[`period${index + 1}_totalPrice`] }}</td>
                            </template>
                        </tr>
                    </tbody>
                </v-table>
            </v-card-text>
        </v-card>
    </v-col>
</template>

<script setup lang="ts">
/*
Organization / ordering applied:
1. Imports
2. Props / Emits
3. Constants
4. Stores & top-level services
5. Reactive state (refs/reactive)
6. Validation rules & setup
7. Computed
8. Handlers / methods
9. Helpers / lifecycle
*/

// 1. Imports
import { computed } from 'vue'

import type { DashboardPermission, DashboardWidget } from '@/stores/dashboard'
import { DASHBOARD_WIDGET_COLUMNS } from '@/views/Dashboard/dashboardUtils'

type ComplaintRow = {
    period?: string
    type?: string
    totalQty?: unknown
    acceptedQty?: unknown
    acceptedTotalPrice?: unknown
}

// 2. Props / Emits
const props = withDefaults(
    defineProps<{
        widget: DashboardWidget
        permissions?: DashboardPermission[]
    }>(),
    { permissions: () => [] }
)

// 7. Computed
const uniquePeriodHeaders = computed(() => {
    const content = props.widget?.body?.content
    const list: ComplaintRow[] = Array.isArray(content) ? (content as ComplaintRow[]) : []
    const periods = list.map((item) => item.period).filter((p): p is string => p != null)
    return periods.filter((item, index) => periods.indexOf(item) === index)
})

const rows = computed(() => {
    const byType = new Map<string, Record<string, unknown>>()

    const content = props.widget?.body?.content
    const list: ComplaintRow[] = Array.isArray(content) ? (content as ComplaintRow[]) : []
    list.forEach((item) => {
        const type = item.type ?? ''
        if (!byType.has(type)) byType.set(type, { type: item.type })
        const row = byType.get(type)!
        const index = uniquePeriodHeaders.value.indexOf(item.period ?? '') + 1
        row[`period${index}_quantity`] = `${item.totalQty}/${item.acceptedQty}`
        row[`period${index}_totalPrice`] = `${item.acceptedTotalPrice}`
    })

    return Array.from(byType.values()).map((row) => {
        uniquePeriodHeaders.value.forEach((_, index) => {
            const keyIndex = index + 1
            if (!row[`period${keyIndex}_quantity`]) row[`period${keyIndex}_quantity`] = '-'
            if (!row[`period${keyIndex}_totalPrice`]) row[`period${keyIndex}_totalPrice`] = '-'
        })
        return row
    })
})

function periodCellClass(index: number): string | null {
    return index % 2 === 0 ? 'four-months-complaints-report__period--shaded' : null
}
</script>

<style scoped>
.four-months-complaints-report__period--shaded {
    background-color: #f5f5f5 !important;
}
</style>
